<template>
  <section class="analyzer-page">
    <div class="analyzer-shell">
      <header class="hero-card panel-card">
        <div>
          <p class="eyebrow">{{ t("pageEyebrow") }}</p>
          <h1>{{ t("pageTitle") }}</h1>
          <p class="lead">{{ t("pageLead") }}</p>
        </div>

        <div class="hero-side">
          <label class="field">
            <span>{{ t("practiceType") }}</span>
            <select v-model="practiceType">
              <option value="suburi">{{ formatPracticeType("suburi") }}</option>
              <option value="shomen-uchi">{{ formatPracticeType("shomen-uchi") }}</option>
              <option value="chudan-no-kamae">{{ formatPracticeType("chudan-no-kamae") }}</option>
            </select>
          </label>

          <label class="field upload-field">
            <span>{{ t("practiceVideo") }}</span>
            <input type="file" accept="video/*" @change="handleFileChange" />
          </label>

          <button class="primary-btn" :disabled="submitting || !selectedFile" @click="submitJob">
            {{ submitting ? t("submitting") : t("startAnalysis") }}
          </button>

          <p v-if="selectedFile" class="hint">{{ t("selectedFile") }}: {{ selectedFile.name }}</p>
          <p v-if="submitError" class="error-panel compact">{{ submitError }}</p>
        </div>
      </header>

      <section class="panel-card">
        <div class="section-head">
          <div>
            <p class="section-tag">{{ t("jobsTag") }}</p>
            <h2>{{ t("recentJobs") }}</h2>
          </div>
          <button class="ghost-btn" :disabled="loadingJobs" @click="loadJobs">
            {{ loadingJobs ? t("refreshing") : t("refresh") }}
          </button>
        </div>

        <p v-if="loadingJobs" class="state-card compact">{{ t("loadingJobs") }}</p>
        <p v-else-if="jobsError" class="error-panel compact">{{ jobsError }}</p>
        <div v-else-if="!jobs.length" class="empty-state">{{ t("emptyJobs") }}</div>

        <div v-else class="job-grid">
          <article v-for="job in jobs" :key="job.jobId" class="job-card">
            <div class="job-topline">
              <span class="soft-badge">{{ formatPracticeType(job.practiceType) }}</span>
              <span :class="['status-chip', job.status]">{{ formatStatus(job.status) }}</span>
            </div>
            <h3>{{ job.originalFileName }}</h3>
            <p>{{ t("jobId") }}: {{ job.jobId }}</p>
            <p>{{ t("createdAt") }}: {{ formatDateTime(job.createdAt) }}</p>
            <div class="job-actions">
              <button class="ghost-btn" @click="openJob(job.jobId)">{{ t("viewResult") }}</button>
              <button
                class="ghost-btn danger-btn"
                :disabled="deletingJobId === job.jobId"
                @click="deleteJob(job.jobId)"
              >
                {{ deletingJobId === job.jobId ? t("deletingJob") : t("deleteJob") }}
              </button>
            </div>
          </article>
        </div>
      </section>
    </div>
  </section>
</template>

<script setup>
import axios from "axios";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import {
  formatPracticeTypeLabel,
  formatStatusLabel,
  localeForLanguage,
  tAnalyzer,
} from "../../shared/analyzerI18n";
import { useSiteLanguage } from "../../store/siteLanguage";
import { API_BASE, ensureApiArray, ensureApiObject } from "./portalShared";

const router = useRouter();
const { lang } = useSiteLanguage();
const currentLocale = computed(() => localeForLanguage(lang.value));
const practiceType = ref("suburi");
const selectedFile = ref(null);
const submitting = ref(false);
const submitError = ref("");
const loadingJobs = ref(false);
const jobsError = ref("");
const jobs = ref([]);
const deletingJobId = ref("");

function t(key) {
  return tAnalyzer(key, lang.value);
}

function handleFileChange(event) {
  const [file] = event.target.files || [];
  selectedFile.value = file || null;
}

function formatPracticeType(value) {
  return formatPracticeTypeLabel(value, lang.value);
}

function formatStatus(value) {
  return formatStatusLabel(value, lang.value);
}

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(currentLocale.value, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

async function loadJobs() {
  loadingJobs.value = true;
  jobsError.value = "";

  try {
    const response = await axios.get(`${API_BASE}/api/analysis/jobs`, { timeout: 10000 });
    jobs.value = ensureApiArray(response, "Analysis jobs API");
  } catch (error) {
    console.error(error);
    jobs.value = [];
    jobsError.value = error?.message || t("jobListFailed");
  } finally {
    loadingJobs.value = false;
  }
}

async function submitJob() {
  if (!selectedFile.value) {
    submitError.value = t("chooseVideoFirst");
    return;
  }

  submitting.value = true;
  submitError.value = "";

  try {
    const formData = new FormData();
    formData.append("video", selectedFile.value);
    formData.append("practiceType", practiceType.value);

    const response = await axios.post(`${API_BASE}/api/analysis/jobs`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "X-Analyzer-Language": lang.value,
      },
      timeout: 120000,
    });

    const job = ensureApiObject(response, "Analysis create API");
    await loadJobs();
    router.push(`/KendouPage/analyzer/${encodeURIComponent(job.jobId)}`);
  } catch (error) {
    console.error(error);
    submitError.value = error?.response?.data?.message || error?.message || t("uploadFailed");
  } finally {
    submitting.value = false;
  }
}

async function deleteJob(jobId) {
  if (!jobId) {
    return;
  }

  if (!window.confirm(t("deleteJobConfirm"))) {
    return;
  }

  deletingJobId.value = jobId;
  jobsError.value = "";

  try {
    await axios.delete(`${API_BASE}/api/analysis/jobs/${encodeURIComponent(jobId)}`, {
      timeout: 10000,
    });
    jobs.value = jobs.value.filter((job) => job.jobId !== jobId);
  } catch (error) {
    console.error(error);
    jobsError.value = error?.response?.data?.message || error?.message || t("deleteJobFailed");
  } finally {
    deletingJobId.value = "";
  }
}

function openJob(jobId) {
  router.push(`/KendouPage/analyzer/${encodeURIComponent(jobId)}`);
}

onMounted(loadJobs);
</script>

<style scoped>
.analyzer-page {
  --paper: rgba(251, 245, 235, 0.92);
  --paper-strong: rgba(255, 250, 242, 0.98);
  --ink: #2f2116;
  --ink-soft: #5d4b3d;
  --line: rgba(182, 148, 110, 0.34);
  min-height: 100vh;
  padding: 20px;
  box-sizing: border-box;
  background:
    radial-gradient(circle at 16% 12%, rgba(255, 255, 255, 0.7), transparent 28%),
    radial-gradient(circle at 86% 82%, rgba(235, 190, 116, 0.28), transparent 26%),
    linear-gradient(135deg, #e7d8bf 0%, #efe6d3 24%, #f5eedf 48%, #eadab8 100%);
}

.analyzer-shell {
  width: 100%;
  max-width: 1380px;
  min-width: 0;
  margin: 0 auto;
  display: grid;
  gap: 18px;
}

.panel-card,
.hero-card,
.state-card,
.error-panel,
.job-card {
  border: 1px solid var(--line);
  border-radius: 28px;
  box-sizing: border-box;
  min-width: 0;
  background: linear-gradient(180deg, var(--paper-strong) 0%, var(--paper) 100%);
  box-shadow:
    0 22px 44px rgba(118, 87, 49, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

.panel-card,
.hero-card {
  padding: 24px;
}

.hero-card {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 20px;
}

.hero-card > * {
  min-width: 0;
}

.hero-side,
.field {
  display: grid;
  gap: 10px;
}

.field span,
.section-tag,
.eyebrow {
  margin: 0;
  font-size: 0.82rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #9c341d;
}

.hero-card h1,
.section-head h2,
.job-card h3 {
  margin: 0;
  color: var(--ink);
}

.hero-card h1 {
  overflow-wrap: anywhere;
}

.lead,
.job-card p,
.hint,
.empty-state {
  color: var(--ink-soft);
  line-height: 1.8;
  overflow-wrap: anywhere;
}

.field select,
.field input,
.primary-btn,
.ghost-btn {
  border-radius: 18px;
  border: 1px solid rgba(177, 144, 108, 0.7);
  padding: 12px 14px;
  font: inherit;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.primary-btn,
.ghost-btn {
  cursor: pointer;
}

.primary-btn {
  background: linear-gradient(135deg, #b64926 0%, #8a2613 100%);
  color: #fff8f3;
}

.ghost-btn {
  background: rgba(255, 249, 240, 0.88);
  color: #5b4636;
}

.danger-btn {
  border-color: rgba(151, 51, 28, 0.45);
  color: #8a2613;
}

.primary-btn:disabled,
.ghost-btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.section-head,
.job-topline {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.job-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.job-card {
  padding: 18px;
  display: grid;
  gap: 10px;
}

.job-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.soft-badge,
.status-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 11px;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
}

.soft-badge {
  background: rgba(235, 220, 201, 0.9);
  color: #6d5138;
}

.status-chip {
  background: linear-gradient(135deg, #442616 0%, #24130b 100%);
  color: #fff7f0;
}

.status-chip.completed {
  background: linear-gradient(135deg, #31593f 0%, #16341f 100%);
}

.compact {
  padding: 14px 16px;
}

@media (max-width: 900px) {
  .hero-card {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 430px) {
  .analyzer-page {
    padding: 14px;
  }

  .panel-card,
  .hero-card {
    padding: 18px;
    border-radius: 24px;
  }

  .hero-card {
    gap: 16px;
  }
}
</style>
